import { resolveRiskAppearance } from '../../constants/riskEventAppearance';

/** 이벤트 팝업 스냅샷 위에 올릴 bbox (감지 프레임 좌표) */
export type CentralRiskEventBbox = {
  bbox: number[];
  frame_width?: number;
  frame_height?: number;
  trackId?: number;
  motionRisk?: boolean;
  riskSubtypeLabel?: string;
  /** 코드명과 함께 알림 메시지에 넣을 설명 */
  riskDescription?: string;
  class_name?: string;
  /** 직접 지정·설정맵 색 (#rrggbb). 있으면 기본 위험 빨강보다 우선 */
  borderColor?: string;
};

export type CentralRiskEventPayload = {
  poolId: number;
  poolName: string;
  cctvId?: number;
  location: string;
  riskType: string;
  riskLabel: string;
  message: string;
  eventDbId?: number | string;
  uiEventId?: string;
  imageDataUrl?: string;
  /** 이벤트 시점 검출 bbox (팝업 스냅샷 오버레이) */
  bboxes?: CentralRiskEventBbox[];
  safetyPhoneRaw?: string;
  source?: 'ai' | 'test' | 'websocket';
  /** 시설 설정에 등록된 경광등 원문 (IP "위치" 형식) */
  poolSirens?: string[];
  /** 위험 감지 시각 (unix sec) — 증거 영상 pre-roll */
  triggerTs?: number;
  /** 직접 지정 bbox 색 (내보내기·캡처 합성에 사용) */
  bboxColor?: string;
  /** 시설 관제 프로필 — 이벤트 종류(bbox) 목록 필터 */
  scenarioProfile?: string | null;
  /** CCTV detection_scenario */
  detectionScenario?: string | null;
  /** CCTV 표시 180도 회전 설정 — 증거 UI 보정용 */
  rotate_180?: boolean;
};

export const DEFAULT_OPERATOR_ALERT_MESSAGE = '<알림>';

/** 시설 전체화면(zIndex 1800)보다 위 — 위험/수동 팝업 공통 */
export const CENTRAL_RISK_DIALOG_Z = 2000;

/** 관제실 경광등 끄기 — 이벤트/알림 전송 창보다 항상 위 (관제사 최우선 인지) */
export const CONTROL_ROOM_SIREN_DIALOG_Z = CENTRAL_RISK_DIALOG_Z + 100;

export function riskTypeToLabel(riskType: string): string {
  return resolveRiskAppearance(riskType).label;
}

/** bbox별 코드명·설명을 알림 본문 줄로 합침 (여러 박스 전부) */
export function formatBboxAlertDetailLines(
  bboxes?: Array<Pick<CentralRiskEventBbox, 'riskSubtypeLabel' | 'riskDescription'>> | null
): string {
  if (!bboxes?.length) return '';
  const lines: string[] = [];
  for (let i = 0; i < bboxes.length; i++) {
    const b = bboxes[i];
    const code = String(b.riskSubtypeLabel || '').trim();
    const desc = String(b.riskDescription || '').trim();
    if (!code && !desc) continue;
    const n = lines.length + 1;
    if (code && desc) lines.push(`${n}) ${code} — ${desc}`);
    else if (code) lines.push(`${n}) ${code}`);
    else lines.push(`${n}) ${desc}`);
  }
  return lines.join('\n');
}

/**
 * 경고/위험 알림 초안.
 * 수영장·익사 등 시설 고정 문구 없이, bbox 코드명·설명을 모두 넣습니다.
 */
export function buildSafetySmsDraft(
  level: 'warning' | 'danger',
  poolName: string,
  location: string,
  bboxes?: Array<Pick<CentralRiskEventBbox, 'riskSubtypeLabel' | 'riskDescription'>> | null
): string {
  const tag = level === 'danger' ? '<위험>' : '<경고>';
  const loc = location?.trim() || '위치 미상';
  const facility = (poolName || '').trim() || '시설';
  const detail = formatBboxAlertDetailLines(bboxes);
  if (detail) {
    return `${tag} ${facility} CCTV ${loc} 이벤트 발생. 즉시 현장 확인.\n${detail}`;
  }
  return `${tag} ${facility} CCTV ${loc} 이벤트 발생. 즉시 현장 확인`;
}

/** 「이벤트」·AI 안내문 등 — 메시지 본문에 넣지 않을 잡음 문구 */
export function isNoiseEventPhrase(raw: string | null | undefined): boolean {
  const p = String(raw || '').trim();
  if (!p) return true;
  if (p === '이벤트') return true;
  if (/^\[AI\]\s*.+에서 이벤트가 발생했습니다\.?$/u.test(p)) return true;
  if (
    /에서 이벤트가 발생했습니다\.?$/u.test(p) &&
    !p.includes('<위험>') &&
    !p.includes('<경고>') &&
    !p.includes('\n')
  ) {
    return true;
  }
  return false;
}

/**
 * 이벤트 팝업용 전체 메시지: 자동 초안 + (선택) 이벤트 문구 + bbox 내역.
 * 수동/자동 모두 기본 이벤트 문구는 비움.
 */
export function buildEventDialogMessage(
  level: 'warning' | 'danger',
  poolName: string,
  location: string,
  bboxes?: Array<Pick<CentralRiskEventBbox, 'riskSubtypeLabel' | 'riskDescription'>> | null,
  eventPhrase?: string | null
): string {
  const tag = level === 'danger' ? '<위험>' : '<경고>';
  const loc = location?.trim() || '위치 미상';
  const facility = (poolName || '').trim() || '시설';
  const header = `${tag} ${facility} CCTV ${loc} 이벤트 발생. 즉시 현장 확인`;
  const phrase = String(eventPhrase || '').trim();
  const detail = formatBboxAlertDetailLines(bboxes);
  const parts: string[] = [header];
  if (
    phrase &&
    !isNoiseEventPhrase(phrase) &&
    phrase !== header &&
    !header.includes(phrase)
  ) {
    parts.push(phrase);
  }
  if (detail) parts.push(detail);
  return parts.join('\n');
}

/** 메시지 본문에서 첫 줄(헤더)만 유지하고 bbox 내역 줄을 다시 붙임 */
export function syncMessageWithBboxDetails(
  currentMessage: string,
  bboxes?: Array<Pick<CentralRiskEventBbox, 'riskSubtypeLabel' | 'riskDescription'>> | null,
  eventPhrase?: string | null
): string {
  const lines = (currentMessage || '').split('\n').map((l) => l.trimEnd());
  const header =
    lines[0]?.trim() ||
    buildSafetySmsDraft('danger', '시설', '위치 미상', null).split('\n')[0];
  const detail = formatBboxAlertDetailLines(bboxes);
  const phrase = String(eventPhrase || '').trim();
  // 기존 번호 목록·빈 줄·잡음 문구 제외한 중간 문장 유지
  const middle = lines
    .slice(1)
    .map((l) => l.trim())
    .filter((l) => l && !/^\d+\)/.test(l) && !isNoiseEventPhrase(l));
  const parts: string[] = [header];
  if (middle.length > 0) {
    parts.push(...middle);
  } else if (phrase && !isNoiseEventPhrase(phrase) && !header.includes(phrase)) {
    parts.push(phrase);
  }
  if (detail) parts.push(detail);
  return parts.join('\n');
}

const DISMISSED_STORAGE_KEY = 'centralRiskDialogDismissed_v1';
const POOL_COOLDOWN_STORAGE_KEY = 'centralRiskDialogPoolCooldown_v1';

/** 이벤트 처리 완료(팝업 닫기) 후 동일 시설 재팝업 억제 시간 */
export const POOL_RISK_POPUP_COOLDOWN_MS = 5 * 60_000;

const dismissedCentralRiskKeys = new Set<string>();
/** poolId → 쿨다운 종료 시각(ms epoch) */
const poolPopupCooldownUntil = new Map<number, number>();

function loadDismissedCentralRiskKeys(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;
    parsed.forEach((k) => {
      if (typeof k === 'string' && k) dismissedCentralRiskKeys.add(k);
    });
  } catch {
    /* ignore */
  }
}

function persistDismissedCentralRiskKeys(): void {
  if (typeof window === 'undefined') return;
  try {
    const list = [...dismissedCentralRiskKeys].slice(-400);
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function pruneExpiredPoolCooldowns(now = Date.now()): void {
  poolPopupCooldownUntil.forEach((until, poolId) => {
    if (until <= now) poolPopupCooldownUntil.delete(poolId);
  });
}

function loadPoolPopupCooldowns(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(POOL_COOLDOWN_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
    const now = Date.now();
    Object.entries(parsed as Record<string, unknown>).forEach(([k, v]) => {
      const poolId = Number(k);
      const until = Number(v);
      if (!Number.isFinite(poolId) || !Number.isFinite(until) || until <= now) return;
      poolPopupCooldownUntil.set(poolId, until);
    });
  } catch {
    /* ignore */
  }
}

function persistPoolPopupCooldowns(): void {
  if (typeof window === 'undefined') return;
  try {
    pruneExpiredPoolCooldowns();
    const obj: Record<string, number> = {};
    poolPopupCooldownUntil.forEach((until, poolId) => {
      obj[String(poolId)] = until;
    });
    sessionStorage.setItem(POOL_COOLDOWN_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* ignore */
  }
}

loadDismissedCentralRiskKeys();
loadPoolPopupCooldowns();

function centralRiskLocationKey(
  poolId: number,
  cctvId: number | undefined,
  riskType: string
): string {
  return `loc:${poolId}:${cctvId ?? 0}:${riskType}`;
}

/** 해당 시설 위험 이벤트 팝업을 cooldownMs 동안 띄우지 않음 */
export function markPoolRiskPopupCooldown(
  poolId: number,
  cooldownMs: number = POOL_RISK_POPUP_COOLDOWN_MS
): void {
  if (!Number.isFinite(poolId)) return;
  const ms = Number.isFinite(cooldownMs) && cooldownMs > 0 ? cooldownMs : POOL_RISK_POPUP_COOLDOWN_MS;
  poolPopupCooldownUntil.set(Number(poolId), Date.now() + ms);
  persistPoolPopupCooldowns();
}

export function isPoolRiskPopupInCooldown(poolId: number): boolean {
  if (!Number.isFinite(poolId)) return false;
  const until = poolPopupCooldownUntil.get(Number(poolId));
  if (until == null) return false;
  if (Date.now() >= until) {
    poolPopupCooldownUntil.delete(Number(poolId));
    persistPoolPopupCooldowns();
    return false;
  }
  return true;
}

export function dismissCentralRiskDialogPayload(payload: CentralRiskEventPayload | null): void {
  if (!payload) return;
  const keys = new Set<string>();
  keys.add(centralRiskLocationKey(payload.poolId, payload.cctvId, payload.riskType));
  if (payload.uiEventId) keys.add(`ui:${payload.uiEventId}`);
  if (payload.eventDbId != null && String(payload.eventDbId).trim() !== '') {
    keys.add(`db:${payload.eventDbId}`);
  }
  keys.forEach((k) => dismissedCentralRiskKeys.add(k));
  persistDismissedCentralRiskKeys();
  // 처리 완료 후 동일 시설은 5분간 재팝업 억제 (오탐 다발 완화)
  markPoolRiskPopupCooldown(payload.poolId);
}

export function isCentralRiskDialogDismissed(
  payload: Pick<
    CentralRiskEventPayload,
    'poolId' | 'cctvId' | 'riskType' | 'uiEventId' | 'eventDbId'
  >
): boolean {
  if (isPoolRiskPopupInCooldown(payload.poolId)) {
    return true;
  }
  if (payload.eventDbId != null && dismissedCentralRiskKeys.has(`db:${payload.eventDbId}`)) {
    return true;
  }
  if (payload.uiEventId && dismissedCentralRiskKeys.has(`ui:${payload.uiEventId}`)) {
    return true;
  }
  return dismissedCentralRiskKeys.has(
    centralRiskLocationKey(payload.poolId, payload.cctvId, payload.riskType)
  );
}

export function isCentralRiskUiEventDismissed(uiEventId: string): boolean {
  return dismissedCentralRiskKeys.has(`ui:${uiEventId}`);
}
